import { useState } from 'react'
import { adminSignIn, requestPin, verifyPin, type AdminStatus } from '../lib/api.js'
import { isInternalHost } from '../lib/hosts.js'

/**
 * Wave9 sign-in.
 *
 * Decision 57: the front door for Wave9 people is **Entra SSO** ("Sign in
 * with Microsoft" → staff session, role user). Decision 149 retired the
 * Wave9-STAFF key from this screen (SSO has bedded in); the ADMIN key stays
 * as the failsafe — and remains the ONLY door to the Admin panel; nothing in
 * an SSO sign-in can mint an admin. Decision 190 retired WAVE9_USER_KEY on
 * the server as well — it no longer opens anything anywhere.
 *
 * ⚠️ TEMPORARY-AUTH still applies to the key. The caller states which door it
 * is knocking on and the server checks that key alone.
 *
 * Decision 136: partner people (resellers/agents) sign in by EMAILED PIN.
 * Decision 149: on a NON-internal host (viewquote.co.uk, any future
 * white-label alias) the partner door is the ONLY thing this screen shows —
 * partners and their customers never meet Wave9 staff sign-in options on the
 * neutral domain. Wave9 people sign in at quotes.wave9.co.uk.
 */

/**
 * Decision 187: carry the current path through sign-in.
 *
 * Someone arriving on `/?quote=<id>` from a HubSpot deal is not signed in yet;
 * before this the callback always landed them on `/` and the quote id was gone,
 * so they had to go back and click the deal's link a second time. The server
 * validates and signs the destination — this only offers it.
 *
 * ⚠️ The `sso_error` marker is stripped: sending someone back to a failure URL
 * after a SUCCESSFUL sign-in would show them an error they no longer have.
 */
function ssoHref(): string {
  const url = new URL(window.location.href)
  url.searchParams.delete('sso_error')
  const next = `${url.pathname}${url.search}`
  return next && next !== '/' ? `/api/auth/login?next=${encodeURIComponent(next)}` : '/api/auth/login'
}

/** Reason codes set by /api/auth/callback when a Microsoft sign-in fails. */
const SSO_ERRORS: Record<string, string> = {
  no_access: 'Your account is set to "No Access" on the portal roster. Ask a Wave9 admin to change your role under Admin → Partners → People.',
  denied: 'Microsoft declined the sign-in. If you expected access, you may not be assigned to the portal — ask for the Portal Ops group.',
  state: 'The sign-in took too long or did not start here. Please try again.',
  exchange: 'Microsoft could not complete the sign-in. Please try again.',
  claims: 'The sign-in response was not valid for this portal.',
  unconfigured: 'Microsoft sign-in is not configured on this deploy.',
}

function ssoErrorFromUrl(): string | null {
  const code = new URLSearchParams(window.location.search).get('sso_error')
  if (!code) return null
  // One-shot: clear it from the address bar so a reload doesn't re-accuse.
  window.history.replaceState(null, '', window.location.pathname)
  return SSO_ERRORS[code] ?? 'Microsoft sign-in failed. Please try again.'
}

export function SignIn({ status, onDone }: { status: AdminStatus; onDone: () => void }) {
  // Decision 149: partner-only screen away from Wave9's own hosts.
  const partnerOnly = !isInternalHost(window.location.hostname)
  // Decision 149: the staff key is retired from the UI — admin key only.
  const keyOffered = status.adminAvailable
  const [key, setKey] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(ssoErrorFromUrl)

  // Decision 136: the partner door — email → emailed 6-digit code → session.
  const [pEmail, setPEmail] = useState('')
  const [pin, setPin] = useState('')
  const [pinSent, setPinSent] = useState<string | null>(null)   // the server's generic message
  const [pBusy, setPBusy] = useState(false)
  const [pError, setPError] = useState<string | null>(null)

  const sendPin = async (e: React.FormEvent) => {
    e.preventDefault()
    setPBusy(true)
    setPError(null)
    try {
      const r = await requestPin(pEmail.trim())
      setPinSent(r.message)
      setPin('')
    } catch (err) {
      setPError((err as Error).message)
    } finally {
      setPBusy(false)
    }
  }

  const submitPin = async (e: React.FormEvent) => {
    e.preventDefault()
    setPBusy(true)
    setPError(null)
    try {
      await verifyPin(pEmail.trim(), pin.trim())
      onDone()
    } catch (err) {
      setPError((err as Error).message)
    } finally {
      setPBusy(false)
    }
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await adminSignIn(key, 'admin')
      setKey('')
      onDone()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const nothingOffered = !status.ssoAvailable && !keyOffered

  return (
    <div className="signin">
      <div className="card" style={{ maxWidth: 520, width: '100%' }}>
        <div className="card__head">
          <div>
            <h2>Wave9 Quote Portal</h2>
            <p className="muted small" style={{ margin: '4px 0 0' }}>Sign in to continue.</p>
          </div>
        </div>
        <div className="card__body">
          {/* Decision 149: Wave9's own doors render on Wave9's own hosts only. */}
          {!partnerOnly && (nothingOffered ? (
            <p className="small" style={{ margin: 0 }}>
              No sign-in method is configured on this deploy. Set the <code>ENTRA_*</code> variables
              for Microsoft sign-in (see <code>docs/SSO-Entra-Setup.md</code>), or{' '}
              <code>ADMIN_KEY</code> for admin key access, in the Netlify
              environment variables and redeploy — environment variables only take effect on a new deploy.
            </p>
          ) : (
            <>
              {status.ssoAvailable && (
                <div style={{ marginBottom: keyOffered ? 20 : 0 }}>
                  <a className="btn btn--primary" href={ssoHref()} style={{ display: 'block', textAlign: 'center' }}>
                    Sign in with Microsoft
                  </a>
                  <p className="muted small" style={{ margin: '8px 0 0' }}>
                    Wave9 staff — use your @wave9.co.uk account. The Admin panel still needs the admin key.
                  </p>
                </div>
              )}

              {/* Decision 149: the Wave9-STAFF key is retired from this screen
                  — Microsoft is the staff door. The ADMIN key stays, as the
                  failsafe and the only route into the Admin panel. */}
              {keyOffered && (
                <form onSubmit={submit}>
                  <p className="small" style={{ margin: '0 0 6px' }}>
                    <strong>Wave9 admin key</strong>{' '}
                    <span className="muted">
                      — failsafe access: everything, plus partners, people, Product Line pricing and scheduled jobs.
                    </span>
                  </p>
                  <label htmlFor="access-key" className="small" style={{ display: 'block', margin: '10px 0 6px' }}>
                    Key
                  </label>
                  <div className="toolbar">
                    <input
                      id="access-key"
                      className="input"
                      type="password"
                      autoComplete="off"
                      value={key}
                      onChange={(e) => setKey(e.target.value)}
                      style={{ flex: 1, minWidth: 220 }}
                    />
                    <button className="btn btn--primary" disabled={busy || !key}>
                      {busy ? 'Checking…' : 'Sign in'}
                    </button>
                  </div>
                </form>
              )}

              {error && <p className="pill pill--stop" style={{ marginTop: 12, display: 'inline-block' }}>{error}</p>}

              <p className="muted small" style={{ marginTop: 16, marginBottom: 0 }}>
                {status.ssoAvailable
                  ? 'Staff sign in with Microsoft — the admin key is the failsafe.'
                  : 'Admin key for now. Microsoft sign-in for Wave9 staff arrives once the Entra registration is set up.'}
              </p>
            </>
          ))}

          {/* Decision 136: the PARTNER door — an emailed 6-digit code, no
              password to leak or reset. Available on every deploy. Decision
              149: on a partner-facing host this is the WHOLE screen. */}
          <div style={partnerOnly ? undefined : { borderTop: '1px solid var(--line)', marginTop: 20, paddingTop: 16 }}>
            <h3 className="small" style={{ margin: '0 0 4px' }}>Partner sign in</h3>
            <p className="muted small" style={{ margin: '0 0 10px' }}>
              Resellers and agents — we email a 6-digit code to your registered address. No password.
            </p>
            {!pinSent ? (
              <form onSubmit={sendPin}>
                <div className="toolbar">
                  <input
                    id="partner-email"
                    className="input"
                    type="email"
                    placeholder="you@yourcompany.co.uk"
                    autoComplete="email"
                    value={pEmail}
                    onChange={(e) => setPEmail(e.target.value)}
                    style={{ flex: 1, minWidth: 220 }}
                  />
                  <button className="btn" disabled={pBusy || !pEmail.includes('@')}>
                    {pBusy ? 'Sending…' : 'Email me a code'}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={submitPin}>
                <p className="muted small" style={{ margin: '0 0 8px' }}>{pinSent}</p>
                <div className="toolbar">
                  <input
                    id="partner-pin"
                    className="input"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    placeholder="6-digit code"
                    autoComplete="one-time-code"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                    style={{ width: 160 }}
                  />
                  <button className="btn btn--primary" disabled={pBusy || pin.length !== 6}>
                    {pBusy ? 'Checking…' : 'Sign in'}
                  </button>
                  <button type="button" className="btn btn--ghost btn--sm" disabled={pBusy}
                    onClick={() => { setPinSent(null); setPin(''); setPError(null) }}>
                    Different address
                  </button>
                </div>
              </form>
            )}
            {pError && <p className="pill pill--stop" style={{ marginTop: 10, display: 'inline-block' }}>{pError}</p>}
            {partnerOnly && (
              <p className="muted small" style={{ marginTop: 14, marginBottom: 0 }}>
                Wave9 staff sign in at quotes.wave9.co.uk.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
